/**
 * Learning API client — study-centre learner pathways (Phase SC-3b).
 *
 * Thin typed wrappers over the authenticated portal `api` client. Every
 * call carries the in-memory access token + host-derived `X-Tenant-Slug`;
 * the BE scopes results to the authenticated learner AND asserts the
 * `study_centre` vertical, so a school/coaching account can never reach
 * this data even if it somehow issued the request.
 */
import { api } from "../api";
import type {
  CoachSummary,
  CreatePathwayInput,
  CourseNavigation,
  CreateReadLogInput,
  CreatePracticeTestInput,
  CreateSkillAssessmentInput,
  CreateStudyGroupInput,
  DiagnosticAnswer,
  DiagnosticResult,
  DiagnosticSession,
  DueCards,
  GenerateProgressReportInput,
  LearnerNotification,
  LearnerNotificationList,
  Pathway,
  PathwayList,
  PathwayStatus,
  PostReviewQuizResult,
  PostReviewQuizSession,
  PracticeTestResult,
  PracticeTestSession,
  ProgressReport,
  ProgressReportList,
  ReadLogEntry,
  ReadLogList,
  ReviewCard,
  RoadmapGenerateResult,
  ResolvedLearningPreferences,
  StartDiagnosticInput,
  StartPostReviewQuizInput,
  SkillAssessmentResult,
  SkillAssessmentSession,
  SkillCertificateList,
  StudyGroup,
  TrackRecordDetail,
  StudyGroupChatProfile,
  StudyGroupList,
  StudyGroupMessage,
  StudyGroupMessageList,
  StudyGroupMemberList,
  StudyGroupMemberStats,
  StudyGroupMessageReceiptList,
  SignedUploadResponse,
  TrackRecordList,
  UpdatePathwayInput,
} from "./types";

const ROOT = "/api/learning";
const BASE = `${ROOT}/pathways`;

export const learningApi = {
  list(params?: { status?: PathwayStatus }): Promise<PathwayList> {
    return api.get<PathwayList>(BASE, { params });
  },

  get(id: string): Promise<Pathway> {
    return api.get<Pathway>(`${BASE}/${id}`);
  },

  create(body: CreatePathwayInput): Promise<Pathway> {
    return api.post<Pathway>(BASE, body);
  },

  update(id: string, body: UpdatePathwayInput): Promise<Pathway> {
    return api.patch<Pathway>(`${BASE}/${id}`, body);
  },

  remove(id: string): Promise<void> {
    return api.delete<void>(`${BASE}/${id}`);
  },

  generateRoadmap(id: string, regenerate = false): Promise<RoadmapGenerateResult> {
    return api.post<RoadmapGenerateResult>(
      `${BASE}/${id}/roadmap/generate`,
      {},
      {
        params: regenerate ? { regenerate: "true" } : undefined,
      },
    );
  },

  getCourseNavigation(pathwayId: string): Promise<CourseNavigation> {
    return api.get<CourseNavigation>(`${BASE}/${pathwayId}/course-navigation`);
  },

  completeCourseLeaf(pathwayId: string, leafId: string): Promise<CourseNavigation> {
    return api.post<CourseNavigation>(
      `${BASE}/${pathwayId}/course-navigation/leaves/${encodeURIComponent(leafId)}/complete`,
      {},
    );
  },
};

/**
 * The daily loop (Phase SC-3d): diagnose → rank → schedule → re-test.
 *
 * `startDiagnostic` is idempotent while a session is open — the BE returns
 * the existing one rather than generating (and charging for) a second
 * quiz, so a double-tap or a remount costs nothing.
 */
export const coachApi = {
  startDiagnostic(pathwayId: string, body: StartDiagnosticInput = {}): Promise<DiagnosticSession> {
    return api.post<DiagnosticSession>(`${BASE}/${pathwayId}/diagnostic`, body);
  },

  learningPreferences(): Promise<ResolvedLearningPreferences> {
    return api.get<ResolvedLearningPreferences>(`${ROOT}/learning-preferences`);
  },

  updateLearningPreferences(body: {
    preferred_language?: ResolvedLearningPreferences["content_language"];
    ziggy_variant?: NonNullable<ResolvedLearningPreferences["ziggy_variant"]>;
  }): Promise<ResolvedLearningPreferences> {
    return api.patch<ResolvedLearningPreferences>(`${ROOT}/learning-preferences`, body);
  },

  trackRecordDetail(entryId: string): Promise<TrackRecordDetail> {
    return api.get<TrackRecordDetail>(`${ROOT}/track-record/${entryId}`);
  },

  openDiagnostic(pathwayId: string): Promise<DiagnosticSession> {
    return api.get<DiagnosticSession>(`${BASE}/${pathwayId}/diagnostic`);
  },

  submitDiagnostic(sessionId: string, answers: DiagnosticAnswer[]): Promise<DiagnosticResult> {
    return api.post<DiagnosticResult>(`${ROOT}/diagnostics/${sessionId}/submit`, { answers });
  },

  dueCards(limit?: number, pathwayId?: string | null): Promise<DueCards> {
    const params: Record<string, string | number> = {};
    if (limit) params.limit = limit;
    if (pathwayId) params.pathway_id = pathwayId;
    return api.get<DueCards>(
      `${ROOT}/cards/due`,
      Object.keys(params).length ? { params } : undefined,
    );
  },

  reviewCard(cardId: string, quality: number): Promise<ReviewCard> {
    return api.post<ReviewCard>(`${ROOT}/cards/${cardId}/review`, { quality });
  },

  startPostReviewQuiz(
    cardId: string,
    body: StartPostReviewQuizInput = {},
  ): Promise<PostReviewQuizSession> {
    return api.post<PostReviewQuizSession>(`${ROOT}/cards/${cardId}/post-quiz`, body);
  },

  submitPostReviewQuiz(
    sessionId: string,
    answers: DiagnosticAnswer[],
  ): Promise<PostReviewQuizResult> {
    return api.post<PostReviewQuizResult>(`${ROOT}/post-review-quizzes/${sessionId}/submit`, {
      answers,
    });
  },

  summary(pathwayId?: string | null): Promise<CoachSummary> {
    return api.get<CoachSummary>(
      `${ROOT}/coach/summary`,
      pathwayId ? { params: { pathway_id: pathwayId } } : undefined,
    );
  },

  logReading(pathwayId: string, body: CreateReadLogInput): Promise<ReadLogEntry> {
    return api.post<ReadLogEntry>(`${BASE}/${pathwayId}/read-log`, body);
  },

  readLogs(params?: { pathway_id?: string; limit?: number }): Promise<ReadLogList> {
    return api.get<ReadLogList>(`${ROOT}/read-logs`, { params });
  },

  startPracticeTest(
    pathwayId: string,
    body: CreatePracticeTestInput,
  ): Promise<PracticeTestSession> {
    return api.post<PracticeTestSession>(`${BASE}/${pathwayId}/practice-tests`, body);
  },

  submitPracticeTest(sessionId: string, answers: DiagnosticAnswer[]): Promise<PracticeTestResult> {
    return api.post<PracticeTestResult>(`${ROOT}/practice-tests/${sessionId}/submit`, { answers });
  },

  trackRecord(params?: { pathway_id?: string; limit?: number }): Promise<TrackRecordList> {
    return api.get<TrackRecordList>(`${ROOT}/track-record`, { params });
  },

  generateProgressReport(
    pathwayId: string,
    body: GenerateProgressReportInput = {},
  ): Promise<ProgressReport> {
    return api.post<ProgressReport>(`${BASE}/${pathwayId}/progress-reports`, body);
  },

  progressReports(params?: { pathway_id?: string; limit?: number }): Promise<ProgressReportList> {
    return api.get<ProgressReportList>(`${ROOT}/progress-reports`, { params });
  },

  getProgressReport(reportId: string): Promise<ProgressReport> {
    return api.get<ProgressReport>(`${ROOT}/progress-reports/${reportId}`);
  },

  notifications(params?: {
    unread_only?: boolean;
    limit?: number;
  }): Promise<LearnerNotificationList> {
    return api.get<LearnerNotificationList>(`${ROOT}/notifications`, { params });
  },

  markNotificationRead(notificationId: string): Promise<LearnerNotification> {
    return api.post<LearnerNotification>(`${ROOT}/notifications/${notificationId}/read`, {});
  },

  startSkillAssessment(
    pathwayId: string,
    body: CreateSkillAssessmentInput,
  ): Promise<SkillAssessmentSession> {
    return api.post<SkillAssessmentSession>(`${BASE}/${pathwayId}/skill-assessments`, body);
  },

  submitSkillAssessment(
    sessionId: string,
    answers: DiagnosticAnswer[],
  ): Promise<SkillAssessmentResult> {
    return api.post<SkillAssessmentResult>(`${ROOT}/skill-assessments/${sessionId}/submit`, {
      answers,
    });
  },

  skillCertificates(): Promise<SkillCertificateList> {
    return api.get<SkillCertificateList>(`${ROOT}/skill-certificates`);
  },

  studyGroups(params?: { pathway_id?: string }): Promise<StudyGroupList> {
    return api.get<StudyGroupList>(`${ROOT}/study-groups`, { params });
  },

  createStudyGroup(body: CreateStudyGroupInput): Promise<StudyGroup> {
    return api.post<StudyGroup>(`${ROOT}/study-groups`, body);
  },

  joinStudyGroup(groupId: string): Promise<StudyGroup> {
    return api.post<StudyGroup>(`${ROOT}/study-groups/${groupId}/join`, {});
  },

  joinStudyGroupByInvite(inviteCode: string): Promise<StudyGroup> {
    return api.post<StudyGroup>(`${ROOT}/study-groups/join-by-invite`, {
      invite_code: inviteCode,
    });
  },

  leaveStudyGroup(groupId: string): Promise<StudyGroup> {
    return api.post<StudyGroup>(`${ROOT}/study-groups/${groupId}/leave`, {});
  },

  studyGroupMessages(groupId: string): Promise<StudyGroupMessageList> {
    return api.get<StudyGroupMessageList>(`${ROOT}/study-groups/${groupId}/messages`);
  },

  postStudyGroupMessage(
    groupId: string,
    payload: {
      body: string;
      mention_user_ids?: string[];
      client_message_id?: string;
    },
  ): Promise<StudyGroupMessage> {
    return api.post<StudyGroupMessage>(`${ROOT}/study-groups/${groupId}/messages`, payload);
  },

  markStudyGroupMessageRead(groupId: string, messageId: string): Promise<StudyGroupMessage> {
    return api.post<StudyGroupMessage>(
      `${ROOT}/study-groups/${groupId}/messages/${messageId}/read`,
      {},
    );
  },

  studyGroupMessageReadBy(
    groupId: string,
    messageId: string,
  ): Promise<{ message_id: string; items: StudyGroupMessage["read_by"]; total: number }> {
    return api.get(`${ROOT}/study-groups/${groupId}/messages/${messageId}/read-by`);
  },

  studyGroupMembers(groupId: string): Promise<StudyGroupMemberList> {
    return api.get<StudyGroupMemberList>(`${ROOT}/study-groups/${groupId}/members`);
  },

  studyGroupMemberStats(groupId: string, memberUserId: string): Promise<StudyGroupMemberStats> {
    return api.get<StudyGroupMemberStats>(
      `${ROOT}/study-groups/${groupId}/members/${memberUserId}/stats`,
    );
  },

  banStudyGroupMember(groupId: string, userId: string): Promise<StudyGroup> {
    return api.post<StudyGroup>(`${ROOT}/study-groups/${groupId}/ban`, { user_id: userId });
  },

  removeStudyGroupMember(groupId: string, userId: string): Promise<StudyGroup> {
    return api.post<StudyGroup>(`${ROOT}/study-groups/${groupId}/remove-member`, {
      user_id: userId,
    });
  },

  updateStudyGroupInviteSettings(
    groupId: string,
    body: { invite_link_mode: "permanent" | "single_use"; regenerate_code?: boolean },
  ): Promise<StudyGroup> {
    return api.put<StudyGroup>(`${ROOT}/study-groups/${groupId}/invite-settings`, body);
  },

  deleteStudyGroupMessage(groupId: string, messageId: string): Promise<StudyGroupMessage> {
    return api.delete<StudyGroupMessage>(`${ROOT}/study-groups/${groupId}/messages/${messageId}`);
  },

  studyGroupChatProfile(groupId: string): Promise<StudyGroupChatProfile> {
    return api.get<StudyGroupChatProfile>(`${ROOT}/study-groups/${groupId}/chat-profile`);
  },

  setStudyGroupChatProfile(
    groupId: string,
    body: { chat_username: string },
  ): Promise<StudyGroupChatProfile> {
    return api.put<StudyGroupChatProfile>(`${ROOT}/study-groups/${groupId}/chat-profile`, body);
  },

  studyGroupMessageReceipts(
    groupId: string,
    messageId: string,
  ): Promise<StudyGroupMessageReceiptList> {
    return api.get(`${ROOT}/study-groups/${groupId}/messages/${messageId}/receipts`);
  },

  promoteStudyGroupModerator(groupId: string, userId: string): Promise<StudyGroup> {
    return api.post<StudyGroup>(`${ROOT}/study-groups/${groupId}/moderators`, {
      user_id: userId,
    });
  },

  demoteStudyGroupModerator(groupId: string, userId: string): Promise<StudyGroup> {
    return api.delete<StudyGroup>(`${ROOT}/study-groups/${groupId}/moderators/${userId}`);
  },

  mintStudyGroupImageUpload(
    groupId: string,
    body: { content_type: string; file_size: number },
  ): Promise<SignedUploadResponse> {
    return api.post<SignedUploadResponse>(
      `${ROOT}/study-groups/${groupId}/messages/upload-url`,
      body,
    );
  },

  postStudyGroupImageMessage(
    groupId: string,
    payload: {
      image_url: string;
      caption?: string;
      mention_user_ids?: string[];
      client_message_id?: string;
    },
  ): Promise<StudyGroupMessage> {
    return api.post<StudyGroupMessage>(`${ROOT}/study-groups/${groupId}/messages/image`, payload);
  },
};
